import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'
import { ENGINEERING_SYSTEMS } from '@/constants/landing'

const Engineering: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const system = ENGINEERING_SYSTEMS[active]

    return (
        <section id='engineering' aria-labelledby='engineering-title' className='relative overflow-hidden bg-ink text-ink-foreground'>
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 top-40 size-[40rem] rounded-full bg-ember/10 blur-[140px]' />

            <div className='relative mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28'>
                <div className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-7'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                            Engineering
                        </p>

                        <h2 id='engineering-title' className='mt-5 font-heading text-[clamp(2.6rem,5.4vw,5rem)] leading-[0.96] tracking-[-0.02em]'>
                            Built like an <em className='italic text-ember'>instrument.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={150} className='flex items-end lg:col-span-5'>
                        <p className='max-w-md text-lg leading-relaxed text-ink-foreground/70'>
                            Every joint, sensor and seam is designed for a home: quiet, soft to the touch, and serviceable for years. Select a part to look closer.
                        </p>
                    </Reveal>
                </div>

                <div className='mt-12 grid gap-3 md:mt-16 lg:grid-cols-12'>
                    <Reveal className='lg:col-span-5'>
                        <div className='relative mx-auto aspect-[3/4] w-full max-w-[34rem] overflow-hidden rounded-[2rem] bg-[oklch(0.2_0.02_50)]'>
                            <Image
                                src='/images/studio.jpg'
                                alt="Studio portrait of Oriel standing in a relaxed pose, showing its ceramic head, oatmeal knit body and graphite joints."
                                fill
                                sizes='(min-width: 1024px) 34rem, 100vw'
                                className='object-cover'
                            />

                            {ENGINEERING_SYSTEMS.map((item, index) => {
                                const selected = index === active

                                return (
                                    <button
                                        key={item.id}
                                        type='button'
                                        onClick={() => setActive(index)}
                                        aria-pressed={selected}
                                        aria-label={`Show ${item.title.toLowerCase()} details`}
                                        style={{ left: `${item.x}%`, top: `${item.y}%` }}
                                        className='group absolute -translate-x-1/2 -translate-y-1/2 rounded-full p-2 focus-visible:outline-2 focus-visible:outline-ember'
                                    >
                                        <span
                                            className={cn(
                                                'relative flex size-7 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-300',
                                                selected
                                                    ? 'border-ember bg-ember text-ink'
                                                    : 'border-white/40 bg-ink/40 text-ink-foreground group-hover:border-ember group-hover:text-ember'
                                            )}
                                        >
                                            {selected && <span className='absolute inset-0 animate-ping-soft rounded-full bg-ember/60' />}
                                            <Plus className={cn('relative size-3.5 transition-transform duration-300', selected && 'rotate-45')} />
                                        </span>
                                    </button>
                                )
                            })}

                            <div className='pointer-events-none absolute inset-x-4 bottom-4 flex items-center justify-between rounded-full border border-white/10 bg-ink/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-foreground/70 backdrop-blur-md'>
                                <span>
                                    Oriel · Oat finish
                                </span>

                                <span className='text-ember'>
                                    {String(active + 1).padStart(2, '0')} / {String(ENGINEERING_SYSTEMS.length).padStart(2, '0')}
                                </span>
                            </div>
                        </div>
                    </Reveal>

                    <div className='grid gap-3 lg:col-span-7 lg:grid-rows-[1fr_auto]'>
                        <Reveal delay={100}>
                            <div className='grid h-full gap-3 rounded-[2rem] border border-white/10 bg-white/[0.03] p-3 md:grid-cols-[minmax(0,15rem)_1fr]'>
                                <ul className='flex gap-1 overflow-x-auto no-scrollbar md:flex-col md:overflow-visible'>
                                    {ENGINEERING_SYSTEMS.map((item, index) => {
                                        const selected = index === active

                                        return (
                                            <li key={item.id} className='shrink-0'>
                                                <button
                                                    type='button'
                                                    onClick={() => setActive(index)}
                                                    aria-pressed={selected}
                                                    className={cn(
                                                        'flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-[15px] transition-colors focus-visible:outline-2 focus-visible:outline-ember',
                                                        selected ? 'bg-ink-foreground text-ink' : 'text-ink-foreground/70 hover:bg-white/5 hover:text-ink-foreground'
                                                    )}
                                                >
                                                    <span className={cn('font-mono text-[11px]', selected ? 'text-ember-deep' : 'text-ink-foreground/40')}>
                                                        {String(index + 1).padStart(2, '0')}
                                                    </span>

                                                    {item.title}
                                                </button>
                                            </li>
                                        )
                                    })}
                                </ul>

                                <div aria-live='polite' className='flex flex-col justify-between gap-8 rounded-[1.5rem] bg-ink p-6 md:p-8'>
                                    <div key={system.id} className='animate-rise'>
                                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                            System {String(active + 1).padStart(2, '0')}
                                        </p>

                                        <h3 className='mt-4 font-heading text-4xl leading-none md:text-5xl'>
                                            {system.title}
                                        </h3>

                                        <p className='mt-5 max-w-lg text-lg leading-relaxed text-ink-foreground/75'>
                                            {system.summary}
                                        </p>
                                    </div>

                                    <ul key={`${system.id}-specs`} className='flex flex-wrap gap-2'>
                                        {system.specs.map((spec, index) => (
                                            <li
                                                key={spec}
                                                style={{ animationDelay: `${150 + index * 90}ms` }}
                                                className='animate-rise rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-xs text-ink-foreground/85'
                                            >
                                                {spec}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={200}>
                            <figure className='relative grid overflow-hidden rounded-[2rem] border border-white/10 md:grid-cols-2'>
                                <div className='relative aspect-[4/3] md:aspect-auto md:min-h-[17rem]'>
                                    <Image
                                        src='/images/detail-joint.jpg'
                                        alt="Macro view of Oriel's knee: a machined graphite actuator where the knit thigh meets the ceramic shin."
                                        fill
                                        sizes='(min-width: 1024px) 28vw, 100vw'
                                        className='object-cover'
                                    />
                                </div>

                                <figcaption className='flex flex-col justify-between gap-6 bg-white/[0.03] p-6 md:p-8'>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                                        Knee actuator
                                    </p>

                                    <p className='font-heading text-2xl leading-snug md:text-[1.75rem]'>
                                        Backdrivable by design. Push against it and it gives way, the way a person's arm would.
                                    </p>

                                    <p className='text-sm text-ink-foreground/60'>
                                        Designed and assembled in-house, rated for 10 years of daily use.
                                    </p>
                                </figcaption>
                            </figure>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Engineering
